import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Chart, ArcElement, DoughnutController,
} from "chart.js";

Chart.register(ArcElement, DoughnutController);

// ── useBreakpoint ─────────────────────────────────────────────────────────────
const useBreakpoint = () => {
  const [width, setWidth] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  if (width < 480)  return "xs";
  if (width < 768)  return "sm";
  if (width < 1024) return "md";
  return "lg";
};

// ── Arrow icons ───────────────────────────────────────────────────────────────
const ArrowUp = ({ color }) => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ display:"inline", verticalAlign:"middle" }}>
    <path d="M4.5 7V2M2 4.5L4.5 2L7 4.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ArrowNeu = ({ color }) => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ display:"inline", verticalAlign:"middle" }}>
    <path d="M1.5 4.5h6" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const ArrowDown = ({ color }) => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ display:"inline", verticalAlign:"middle" }}>
    <path d="M4.5 2v5M2 4.5L4.5 7L7 4.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ── Badge ─────────────────────────────────────────────────────────────────────
const Badge = ({ type, label, isDark }) => {
  const Arrow = type === "Up" ? ArrowUp : type === "Neu" ? ArrowNeu : ArrowDown;
  const map = {
    Up:   { bg: isDark ? "rgba(40,179,180,0.2)"  : "rgba(40,179,180,0.12)", color: isDark ? "#9FE1CB" : "#085041" },
    Neu:  { bg: isDark ? "rgba(214,223,39,0.18)" : "rgba(214,223,39,0.14)", color: isDark ? "#D6DF27" : "#633806" },
    Down: { bg: isDark ? "rgba(226,75,74,0.2)"   : "rgba(226,75,74,0.1)",  color: isDark ? "#F09595" : "#791F1F" },
  };
  const s = map[type] || map.Neu;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4,
      fontSize:11, fontWeight:600, padding:"3px 8px",
      borderRadius:20, background:s.bg, color:s.color, whiteSpace:"nowrap",
    }}>
      <Arrow color={s.color} />
      {label}
    </span>
  );
};

// ── RingChart ─────────────────────────────────────────────────────────────────
const RingChart = ({ pct, accent, trackColor, size = 56 }) => {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        datasets: [{
          data: [pct, 100 - pct],
          backgroundColor: [accent, trackColor],
          borderWidth: 0,
          borderRadius: [4, 0],
        }],
      },
      options: {
        responsive: false,
        cutout: "72%",
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        animation: { duration: 700, easing: "easeOutQuart" },
      },
    });
    return () => chartRef.current?.destroy();
  }, [pct, accent, trackColor]);

  return (
    <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
      <canvas ref={canvasRef} width={size} height={size} />
      <div style={{
        position:"absolute", inset:0,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize: size < 50 ? 9 : 10, fontWeight:700, color:accent,
      }}>
        {pct}%
      </div>
    </div>
  );
};

// ── MiniBar ───────────────────────────────────────────────────────────────────
const MiniBar = ({ pct, accent, trackColor, label, labelColor }) => (
  <div style={{ marginTop:10 }}>
    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
      <span style={{ fontSize:10, color:labelColor }}>{label}</span>
      <span style={{ fontSize:10, fontWeight:700, color:accent }}>{pct}%</span>
    </div>
    <div style={{ height:4, borderRadius:4, background:trackColor, overflow:"hidden" }}>
      <div style={{ width:`${pct}%`, height:"100%", borderRadius:4, background:accent, transition:"width .6s ease" }} />
    </div>
  </div>
);

// ── KpiCard shell ─────────────────────────────────────────────────────────────
const KpiCard = ({ label, accent, surface, border, labelColor, children }) => (
  <div style={{
    background: surface,
    border: `1px solid ${border}`,
    borderRadius:12,
    padding:"16px 18px",
    position:"relative",
    overflow:"hidden",
    minWidth:0,
  }}>
    <div style={{
      position:"absolute", top:0, left:0, right:0,
      height:3, background:accent, borderRadius:"12px 12px 0 0",
    }} />
    <div style={{
      fontSize:11, fontWeight:600, letterSpacing:".06em",
      textTransform:"uppercase", color:labelColor, marginBottom:10,
    }}>
      {label}
    </div>
    {children}
  </div>
);

// ── KpiCards ──────────────────────────────────────────────────────────────────
const KpiCards = ({ metrics }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";
  const bp     = useBreakpoint();

  if (!metrics) return null;

  const { totalAllocations, totalFte, activeAllocations, averageFte } = metrics;

  const activePct   = totalAllocations ? Math.round((activeAllocations / totalAllocations) * 100) : 0;
  const ftePct      = Math.min(100, Math.round(averageFte * 100));
  const fteAvgPct   = Math.min(100, Math.round((totalFte / totalAllocations) * 100));
  const avgFteLabel = averageFte >= 0.75 ? "High utilisation" : averageFte >= 0.5 ? "Moderate" : "Low utilisation";
  const avgFteType  = averageFte >= 0.75 ? "Up" : averageFte >= 0.5 ? "Neu" : "Down";

  const cols     = bp === "xs" ? 1 : bp === "sm" ? 2 : 4;
  const ringSize = bp === "xs" ? 48 : 56;

  // ── theme-aware accent colors ─────────────────────────────────────────────
  const TEAL  = t.accent;                                          // #28B3B4
  const GOLD  = t.gold;                                            // #D6DF27
  const BLUE  = isDark ? "#85B7EB"  : "#378ADD";
  const GREEN = isDark ? "#97C459"  : "#639922";

  const TEAL_TRACK  = isDark ? "rgba(40,179,180,0.18)"  : "rgba(40,179,180,0.12)";
  const GOLD_TRACK  = t.gold2;
  const BLUE_TRACK  = isDark ? "rgba(133,183,235,0.18)" : "rgba(55,138,221,0.12)";
  const GREEN_TRACK = isDark ? "rgba(151,196,89,0.18)"  : "rgba(99,153,34,0.14)";

  const valStyle = (size = 28) => ({
    fontSize: bp === "xs" ? Math.round(size * 0.85) : size,
    fontWeight:700, color:t.text, lineHeight:1,
    marginBottom:4, fontVariantNumeric:"tabular-nums",
  });

  const cardBase = { surface: t.surface, border: t.border, labelColor: t.text3 };

  return (
    <div style={{
      display:"grid",
      gridTemplateColumns:`repeat(${cols}, minmax(0,1fr))`,
      gap: bp === "xs" ? 8 : 12,
      width:"100%",
    }}>

      {/* 1 — Total Allocations */}
      <KpiCard label="Total Allocations" accent={TEAL} {...cardBase}>
        <div style={valStyle()}>{totalAllocations}</div>
        <div style={{ fontSize:12, color:t.text2, marginBottom:10 }}>Allocations this month</div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:11, color:t.text3, marginBottom:4 }}>{activeAllocations} active</div>
            <Badge type="Up" label={`${activePct}% active`} isDark={isDark} />
          </div>
          <RingChart pct={activePct} accent={TEAL} trackColor={TEAL_TRACK} size={ringSize} />
        </div>
      </KpiCard>

      {/* 2 — Total FTE */}
      <KpiCard label="Total FTE" accent={GOLD} {...cardBase}>
        <div style={valStyle()}>{Number(totalFte).toFixed(1)}</div>
        <div style={{ fontSize:12, color:t.text2, marginBottom:8 }}>Full-time equivalents</div>
        <Badge type="Neu" label={`${(totalFte / totalAllocations).toFixed(2)} avg / head`} isDark={isDark} />
        <MiniBar
          pct={fteAvgPct}
          accent={GOLD}
          trackColor={GOLD_TRACK}
          label="utilisation"
          labelColor={t.text3}
        />
      </KpiCard>

      {/* 3 — Active Allocations */}
      <KpiCard label="Active Allocations" accent={BLUE} {...cardBase}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
          <div>
            <div style={valStyle()}>{activeAllocations}</div>
            <div style={{ fontSize:12, color:t.text2 }}>Currently active</div>
          </div>
          <div style={{ textAlign:"right", flexShrink:0 }}>
            <div style={{ fontSize: bp === "xs" ? 28 : 34, fontWeight:700, color:BLUE, lineHeight:1 }}>
              {activePct}%
            </div>
            <div style={{ fontSize:11, color:t.text3 }}>of total</div>
          </div>
        </div>
        <div style={{ marginTop:12 }}>
          <div style={{ height:6, borderRadius:3, background:BLUE_TRACK, overflow:"hidden" }}>
            <div style={{ width:`${activePct}%`, height:"100%", borderRadius:3, background:BLUE, transition:"width .6s ease" }} />
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
            <span style={{ fontSize:10, color:t.text3 }}>0</span>
            <span style={{ fontSize:10, color:t.text3 }}>{totalAllocations} total</span>
          </div>
        </div>
      </KpiCard>

      {/* 4 — Average FTE */}
      <KpiCard label="Average FTE" accent={GREEN} {...cardBase}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
          <div>
            <div style={valStyle()}>{Number(averageFte).toFixed(2)}</div>
            <div style={{ fontSize:12, color:t.text2, marginBottom:8 }}>Per allocation</div>
            <Badge type={avgFteType} label={avgFteLabel} isDark={isDark} />
          </div>
          <RingChart pct={ftePct} accent={GREEN} trackColor={GREEN_TRACK} size={ringSize} />
        </div>
      </KpiCard>

    </div>
  );
};

export default KpiCards;